import { Injectable } from '@angular/core';
import { MerakisService } from '@app/features/merakis/shared/merakis.service';
import { LocationsService } from '@app/features/locations/shared/locations.service';
import { Location } from '@app/shared/models/location.model';
import { MerakiSwitchStatus } from '@app/shared/models/meraki/meraki-switch-status.model';
import { MerakiLocation } from '@app/shared/models/meraki/meraki-location.model';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ChainWideMerakisService {
    constructor(
        private merakisService: MerakisService,
        private locationsService: LocationsService
    ) { }

    async getAllSwitches(): Promise<MerakiSwitchStatus[]> {
        const allSwitches: MerakiSwitchStatus[] = [];
        try {
            const observable = await this.merakisService.getAllSwitches();
            const merakis_from_response = await firstValueFrom(observable);
            if (typeof merakis_from_response === 'string') {
                console.log("Merakis timed out!")
                return allSwitches;
            }
            merakis_from_response["message"].forEach(meraki_json => {
                let meraki: MerakiSwitchStatus = meraki_json;
                allSwitches.push(meraki);
            });
        } catch (error) {
            console.log("Merakis timed out!")
        }
        return allSwitches;
    }

    async getAllLocations(): Promise<Location[]> {
        const locations: Location[] = [];
        try {
            const observable = await this.locationsService.getAllLocationData();
            const locations_from_response = await firstValueFrom(observable);
            if (typeof locations_from_response === 'string') {
                console.log("Locations timed out!");
                return locations;
            }
            locations_from_response["message"]["locationList"].forEach(locations_json => {
                let location: Location = new Location;
                location.name = locations_json["core"]["locationName"];
                location.number = locations_json["locationNumber"];
                location.status = locations_json["locationStatus"]["locationStatus"];

                if (!locations_json["core"].hasOwnProperty('timeZone')
                    || !locations_json["core"]["timeZone"].hasOwnProperty('timeZoneName')) {
                    location.timezone = "";
                } else {
                    location.timezone = locations_json["core"]["timeZone"]["timeZoneName"].split(" ")[2];
                }

                // Some locations don't have operators, such as locations with a 'future' state
                if (!locations_json.hasOwnProperty('operator')) {
                    location.operatorName = "";
                } else {
                    location.operatorName = locations_json["operator"]["name"];
                }
                locations.push(location);
            });
        } catch (error) {
            console.log(error);
        }
        return locations;
    }

    private sortSwitches(allSwitches: MerakiSwitchStatus[]): Map<string, MerakiSwitchStatus[]> {
        let sortedSwitches = new Map<string, MerakiSwitchStatus[]>();
        for (let meraki of allSwitches) {
            let storeNumber = "";

            if (meraki.name != null) {
                storeNumber = meraki.name.substring(0, 5);
            }

            if (sortedSwitches.has(storeNumber)) {
                sortedSwitches.get(storeNumber).push(meraki);
            } else {
                let switches = [];
                switches.push(meraki);
                sortedSwitches.set(storeNumber, switches);
            }
        }
        return sortedSwitches;
    }

    private sortLocations(locations: Location[]): Map<string, Location> {
        let sortedLocations = new Map<string, Location>();
        locations.forEach(location => {
            sortedLocations.set(location.number, location);
        });
        return sortedLocations;
    }

    async createDataSource(locations?: Location[]): Promise<MerakiLocation[]> {
        const merakiLocations: MerakiLocation[] = [];
        let locationsToUse: Location[];

        if (locations == null) {
            locationsToUse = await this.getAllLocations();
        } else {
            locationsToUse = locations;
        }

        const allSwitches = await this.getAllSwitches();
        const sortedSwitches = this.sortSwitches(allSwitches);
        const sortedLocation = this.sortLocations(locationsToUse);

        sortedSwitches.forEach((value: MerakiSwitchStatus[], key: string) => {
            let merakiLocation: MerakiLocation = new MerakiLocation;
            merakiLocation.merakis = value;

            if (sortedLocation.has(key)) {
                merakiLocation.location = sortedLocation.get(key);
            } else {
                let location: Location = new Location;
                location.name = "";
                location.number = "";
                location.operatorName = "";
                location.status = "";
                location.timezone = "";
                merakiLocation.location = location;
            }
            merakiLocations.push(merakiLocation);
        });

        return merakiLocations;
    }
}
